import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { signUp, signInWithGoogle } from '../../utils/supabase';
// Import des icônes pour améliorer l'interface
import { EyeIcon, EyeSlashIcon, EnvelopeIcon, LockClosedIcon, UserIcon } from '@heroicons/react/24/outline';
import AuthAside from './AuthAside';
// Thème citron / noir / blanc partagé avec la page de connexion (classes "auth-")
import './AuthPages.css';

// Arguments affichés dans le panneau noir (offre réelle : voir constants/plans.js)
const AUTH_HIGHLIGHTS = [
  'Essai gratuit de 14 jours, sans engagement',
  '30 points IA offerts à l\'inscription',
  'Connexion WhatsApp via votre fournisseur API',
  'Commandes, livraisons et stocks dans un seul outil'
];

/**
 * Champ de mot de passe avec bouton afficher/masquer (évite de dupliquer le bloc pour la confirmation).
 */
const PasswordField = ({ id, label, value, onChange, visible, onToggle }) => (
  <>
    <label htmlFor={id} className="auth-label">{label}</label>
    <div className="auth-field">
      <LockClosedIcon className="auth-field-icon" />
      <input
        id={id}
        name={id}
        type={visible ? 'text' : 'password'}
        required
        value={value}
        onChange={onChange}
        placeholder="••••••••"
      />
      <button
        type="button"
        onClick={onToggle}
        className="auth-eye"
        aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
      >
        {visible ? <EyeSlashIcon /> : <EyeIcon />}
      </button>
    </div>
  </>
);

const RegisterForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false); // État pour le bouton Google
  // États pour gérer l'affichage des mots de passe
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  // Fonctions pour basculer l'affichage des mots de passe
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Fonction pour gérer l'inscription avec Google
  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    setError(null);

    try {
      console.log('Attempting Google sign up...');
      const redirect = new URLSearchParams(location.search).get('redirect');
      const { error } = await signInWithGoogle({
        redirectTo: redirect ? `${window.location.origin}${redirect}` : undefined
      });
      
      if (error) {
        console.error('Google sign up error:', error);
        throw error;
      }
      
      // La redirection sera gérée automatiquement par Supabase
      console.log('Google sign up initiated successfully');
    } catch (err) {
      console.error('Google authentication error:', err);
      setError(err.message || 'Erreur lors de l\'inscription avec Google');
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      const redirect = new URLSearchParams(location.search).get('redirect');
      const { error } = await signUp({
        email: formData.email,
        password: formData.password,
        // Transmet le nom saisi afin qu'il soit conservé avec le compte Supabase.
        fullName: formData.fullName,
        emailRedirectTo: redirect ? `${window.location.origin}${redirect}` : undefined
      });

      if (error) throw error;

      navigate(redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login', {
        replace: true,
        state: { message: 'Inscription réussie ! Veuillez vérifier votre email pour confirmer votre compte.' }
      });
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Une erreur est survenue lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-shell">
        {/* Panneau de présentation (masqué sur mobile) */}
        <AuthAside titre="Lancez votre boutique avec un essai de" motCle="14 jours" points={AUTH_HIGHLIGHTS} />

        {/* Formulaire d'inscription */}
        <section className="auth-card">
          <h2>Rejoignez-nous</h2>
          <p className="auth-sub">Créer votre compte</p>

          <button
            onClick={handleGoogleSignUp}
            disabled={googleLoading}
            className="auth-btn auth-btn-outline"
          >
            {googleLoading ? (
              <>
                <span className="auth-spinner auth-spinner-dark" />
                Inscription en cours...
              </>
            ) : (
              <>
                {/* Icône Google SVG */}
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuer avec Google
              </>
            )}
          </button>

          <div className="auth-divider"><span>ou s'inscrire avec email</span></div>

          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="auth-alert auth-alert-error" role="alert">{error}</div>}

            <label htmlFor="fullName" className="auth-label">Nom complet</label>
            <div className="auth-field">
              <UserIcon className="auth-field-icon" />
              <input
                id="fullName"
                name="fullName"
                type="text"
                required
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Votre nom complet"
              />
            </div>

            <label htmlFor="email" className="auth-label">Email</label>
            <div className="auth-field">
              <EnvelopeIcon className="auth-field-icon" />
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="votre.email@exemple.com"
              />
            </div>

            <PasswordField
              id="password"
              label="Mot de passe"
              value={formData.password}
              onChange={handleChange}
              visible={showPassword}
              onToggle={togglePasswordVisibility}
            />
            <PasswordField
              id="confirmPassword"
              label="Confirmer le mot de passe"
              value={formData.confirmPassword}
              onChange={handleChange}
              visible={showConfirmPassword}
              onToggle={toggleConfirmPasswordVisibility}
            />

            <button type="submit" disabled={loading} className="auth-btn auth-btn-accent">
              {loading ? (
                <>
                  <span className="auth-spinner" />
                  Inscription...
                </>
              ) : (
                'S\'inscrire'
              )}
            </button>
          </form>

          <p className="auth-switch">
            Déjà inscrit ? <Link to="/login">Se connecter</Link>
          </p>
        </section>
      </div>
    </div>
  );
};

export default RegisterForm;
